function vpTotalsFormatMoney(cents) {
  const amount = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0;

  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    const format = window.theme?.moneyFormat || window.Shopify?.money_format;
    return format ? Shopify.formatMoney(amount, format) : Shopify.formatMoney(amount);
  }

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(2)}`;
  }
}

function vpTotalsEscape(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function vpIsDiscountCodeApplication(app) {
  const type = String(app?.type || '').toLowerCase();
  return type === 'discount_code' || type === 'discountcode';
}

function vpFormatDiscountSummary(app) {
  const valueType = String(app?.value_type || '').toLowerCase();
  const raw = app?.value;

  if (valueType === 'percentage') {
    const pct = parseFloat(raw);
    if (Number.isNaN(pct)) return '';
    const display = Number.isInteger(pct) ? String(pct) : String(pct).replace(/\.0+$/, '');
    return `${display}% off`;
  }

  if (valueType === 'fixed_amount') {
    const allocated = app?.total_allocated_amount;
    if (typeof allocated === 'number' && allocated > 0) {
      return `${vpTotalsFormatMoney(allocated)} off`;
    }

    let amount = parseFloat(raw);
    if (Number.isNaN(amount) || amount <= 0) return '';

    // Ajax Cart API usually returns fixed amounts in currency units (e.g. "15.0").
    if (String(raw).includes('.') || amount < 1000) {
      amount = Math.round(amount * 100);
    }

    return `${vpTotalsFormatMoney(amount)} off`;
  }

  return '';
}

function vpCollectDiscountCodes(cart) {
  // Case-insensitive dedupe: Shopify can return the same code as
  // discount_codes[i].code (e.g. TEST15%) and application.title (e.g. test15%).
  const byKey = new Map();

  const add = (rawCode, summary = '') => {
    const code = String(rawCode || '').trim();
    if (!code) return;
    const key = code.toUpperCase();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { code, summary: summary || '' });
      return;
    }
    if (!existing.summary && summary) {
      existing.summary = summary;
    }
  };

  const addFromApp = (app) => {
    if (!vpIsDiscountCodeApplication(app)) return;
    add(app.title || app.code, vpFormatDiscountSummary(app));
  };

  (cart?.cart_level_discount_applications || []).forEach(addFromApp);

  (cart?.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      addFromApp(allocation?.discount_application);
    });
  });

  // Shopify keeps attempted codes in discount_codes even when invalid.
  // Only codes with applicable === true are actually applied.
  (cart?.discount_codes || []).forEach((entry) => {
    if (entry?.code && entry.applicable === true) {
      add(entry.code);
    }
  });

  return [...byKey.values()];
}

function vpCartHasDiscountCode(cart, code) {
  const needle = String(code || '')
    .trim()
    .toUpperCase();
  if (!needle) return false;

  return vpCollectDiscountCodes(cart).some((entry) => String(entry.code || '').toUpperCase() === needle);
}

class VpCartTotals {
  constructor(cartDrawer) {
    this.cartDrawer = cartDrawer;
    this.error = '';
    this.draft = '';
    this.busy = false;
    this.bound = false;
  }

  get cart() {
    return this.cartDrawer.state?.cart;
  }

  get progressRoot() {
    return this.cartDrawer.querySelector('[data-vp-cart-progress]');
  }

  get discountRoot() {
    return this.cartDrawer.querySelector('[data-vp-cart-discount]');
  }

  get totalsRoot() {
    return this.cartDrawer.querySelector('[data-vp-cart-totals]');
  }

  bindOnce() {
    if (this.bound) return;
    this.bound = true;

    this.cartDrawer.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;

      const applyBtn = target.closest('[data-vp-discount-apply]');
      if (applyBtn && this.cartDrawer.contains(applyBtn)) {
        event.preventDefault();
        this.applyDiscount();
        return;
      }

      const removeBtn = target.closest('[data-vp-discount-remove]');
      if (removeBtn && this.cartDrawer.contains(removeBtn)) {
        event.preventDefault();
        this.removeDiscount();
      }
    });

    this.cartDrawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest('[data-vp-discount-input]');
      if (!input || !this.cartDrawer.contains(input)) return;
      event.preventDefault();
      this.applyDiscount();
    });

    this.cartDrawer.addEventListener('input', (event) => {
      const input = event.target.closest('[data-vp-discount-input]');
      if (!input || !this.cartDrawer.contains(input)) return;
      this.draft = input.value;
      if (this.error) {
        this.error = '';
        const err = this.discountRoot?.querySelector('[data-vp-discount-error]');
        if (err) {
          err.hidden = true;
          err.textContent = '';
        }
      }
    });
  }

  render() {
    this.bindOnce();
    this.renderProgress();
    this.renderDiscount();
    this.renderTotals();
  }

  renderProgress() {
    const root = this.progressRoot;
    const cart = this.cart;
    if (!root || !cart?.item_count) return;

    const unlocked = cart.item_count >= 2;
    const text = root.querySelector('.vp-cart-drawer__prog-text');
    const bar = root.querySelector('.vp-cart-drawer__prog-bar i');
    const lockedHtml = root.dataset.textLocked || '';
    const unlockedHtml = root.dataset.textUnlocked || '';

    if (text) {
      text.innerHTML = unlocked ? unlockedHtml : lockedHtml;
    }

    if (bar) bar.style.width = unlocked ? '100%' : '50%';
    root.dataset.itemCount = String(cart.item_count);
  }

  renderDiscount() {
    const root = this.discountRoot;
    const cart = this.cart;
    if (!root) return;

    if (!cart?.item_count) {
      root.innerHTML = '';
      root.hidden = true;
      return;
    }

    root.hidden = false;
    const codes = vpCollectDiscountCodes(cart);

    if (codes.length) {
      const errHtml = this.error
        ? `<p class="vp-cart-drawer__code-err" data-vp-discount-error role="alert">${vpTotalsEscape(
            this.error
          )}</p>`
        : '';
      root.innerHTML =
        codes
          .map((entry) => {
            const summary = entry.summary ? `, ${vpTotalsEscape(entry.summary)}` : '';
            return `<div class="vp-cart-drawer__applied">
            <span><b>${vpTotalsEscape(entry.code)}</b> applied${summary}</span>
            <button type="button" class="vp-cart-drawer__applied-remove" data-vp-discount-remove${
              this.busy ? ' disabled' : ''
            }>
              Remove
            </button>
          </div>`;
          })
          .join('') + errHtml;
      return;
    }

    const errHtml = this.error
      ? `<p class="vp-cart-drawer__code-err" data-vp-discount-error role="alert">${vpTotalsEscape(
          this.error
        )}</p>`
      : `<p class="vp-cart-drawer__code-err" data-vp-discount-error hidden></p>`;

    root.innerHTML = `<div class="vp-cart-drawer__code">
      <input
        type="text"
        name="discount"
        placeholder="Discount code"
        autocomplete="off"
        autocapitalize="characters"
        spellcheck="false"
        data-vp-discount-input
        aria-label="Discount code"
        value="${vpTotalsEscape(this.draft)}"
        ${this.busy ? 'disabled' : ''}
      >
      <button type="button" data-vp-discount-apply${this.busy ? ' disabled' : ''}>Apply</button>
    </div>${errHtml}`;
  }

  renderTotals() {
    const root = this.totalsRoot;
    const cart = this.cart;
    if (!root) return;

    if (!cart?.item_count) {
      root.innerHTML = '';
      return;
    }

    const subtotal = vpTotalsFormatMoney(cart.items_subtotal_price);
    const total = vpTotalsFormatMoney(cart.total_price);
    const discount = cart.total_discount || 0;
    const discountMoney = vpTotalsFormatMoney(discount);

    const showShipping = root.dataset.vpShowShipping !== 'false';

    const discountsRow =
      discount > 0
        ? `<div class="vp-cart-drawer__tot" data-vp-tot-discounts-row>
            <span>Discounts</span>
            <span class="vp-cart-drawer__tot-save" data-vp-tot-discounts>-${discountMoney}</span>
          </div>`
        : '';

    const shippingRow = showShipping
      ? `<div class="vp-cart-drawer__tot is--shipping">
            <span>Shipping</span>
            <span>Free</span>
          </div>`
      : '';

    const savingRow =
      discount > 0
        ? `<div class="vp-cart-drawer__tot vp-cart-drawer__tot--saved" data-vp-tot-saving-row>
            <span>You are saving</span>
            <span data-vp-tot-saving>${discountMoney}</span>
          </div>`
        : '';

    root.innerHTML = `
      <div class="vp-cart-drawer__tot">
        <span>Subtotal</span>
        <span data-vp-tot-subtotal>${subtotal}</span>
      </div>
      ${discountsRow}
      ${shippingRow}
      ${savingRow}
      <div class="vp-cart-drawer__tot vp-cart-drawer__tot--big">
        <span>Total</span>
        <span data-vp-tot-total>${total}</span>
      </div>
    `;
  }

  async applyDiscount() {
    if (this.busy) return;

    const input = this.discountRoot?.querySelector('[data-vp-discount-input]');
    const code = (input?.value || this.draft || '').trim().toUpperCase();

    if (!code) {
      this.error = 'Enter a discount code.';
      this.renderDiscount();
      return;
    }

    this.busy = true;
    this.error = '';
    this.draft = code;
    this.renderDiscount();

    try {
      let cart = await this.cartDrawer.mutateCart(() =>
        window.VPCartAPI.updateCart({ discount: code })
      );

      if (!vpCartHasDiscountCode(cart, code)) {
        // Clear the attempted code so it does not linger as "applied" in the UI.
        try {
          cart = await this.cartDrawer.mutateCart(() => window.VPCartAPI.clearDiscount());
        } catch (clearError) {
          console.error('[vp-cart-totals] Failed to clear invalid code', clearError);
        }

        this.error = 'That code is not valid.';
        this.busy = false;
        this.renderDiscount();
        this.renderTotals();
        return;
      }

      this.draft = '';
      this.error = '';
      await this.cartDrawer.refreshDrawerInner();
      this.render();
      this.cartDrawer.renderUpsell();
    } catch (error) {
      console.error('[vp-cart-totals] Failed to apply discount', error);
      this.error = 'That code is not valid.';
    } finally {
      this.busy = false;
      this.renderDiscount();
    }
  }

  async removeDiscount() {
    if (this.busy) return;

    this.busy = true;
    this.error = '';
    this.renderDiscount();

    try {
      const cart = await this.cartDrawer.mutateCart(() => window.VPCartAPI.clearDiscount());
      this.draft = '';

      if (vpCollectDiscountCodes(cart).length) {
        this.error = 'Could not remove code. Try again.';
        this.renderDiscount();
        this.renderTotals();
        return;
      }

      await this.cartDrawer.refreshDrawerInner();
      this.render();
      this.cartDrawer.renderUpsell();
    } catch (error) {
      console.error('[vp-cart-totals] Failed to remove discount', error);
      this.error = 'Could not remove code. Try again.';
    } finally {
      this.busy = false;
      this.renderDiscount();
    }
  }
}

window.VpCartTotals = VpCartTotals;
window.vpCollectDiscountCodes = vpCollectDiscountCodes;
window.vpCartHasDiscountCode = vpCartHasDiscountCode;
